import { useEffect, useState, ReactNode, ReactElement, JSX } from 'react';
import { CircularProgress, Box } from '@mui/material';
import SuspenseLoader from 'src/components/SuspenseLoader';

interface RouteDataLoaderProps<T = any> {
  /**
   * Function that fetches the data needed for the route
   * @returns Promise that resolves to the data to be passed to children
   */
  fetchData: () => Promise<T>;
  /**
   * Children components that will receive the fetched data
   */
  children: (data: T) => ReactNode;
  /**
   * Optional loading component. If not provided, uses SuspenseLoader
   */
  loadingComponent?: ReactNode;
  /**
   * Optional error handler. If provided, errors will be caught and handled
   */
  onError?: (error: Error) => void;
}

/**
 * RouteDataLoader - A component that fetches data before rendering route children
 * 
 * Usage:
 * ```tsx
 * <RouteDataLoader
 *   fetchData={async () => {
 *     const response = await fetch('/api/data');
 *     return response.json();
 *   }}
 * >
 *   {(data) => <YourComponent data={data} />}
 * </RouteDataLoader>
 * ```
 */
export function RouteDataLoader<T = any>({
  fetchData,
  children,
  loadingComponent,
  onError
}: RouteDataLoaderProps<T>): JSX.Element {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        const fetchedData = await fetchData();
        
        if (isMounted) {
          setData(fetchedData);
          setLoading(false);
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to fetch data');
        if (isMounted) {
          setError(error);
          setLoading(false);
          if (onError) {
            onError(error);
          } else {
            console.error('RouteDataLoader error:', error);
          }
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [fetchData, onError]);

  if (loading) {
    return (loadingComponent || <SuspenseLoader />) as JSX.Element;
  }

  if (error && !onError) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
          flexDirection: 'column',
          gap: 2
        }}
      >
        <CircularProgress />
        <div>Error loading data: {error.message}</div>
      </Box>
    );
  }

  if (error) {
    // If error handler is provided, render children anyway (handler may have dealt with it)
    return <>{children(data as T)}</> as JSX.Element;
  }

  return <>{children(data as T)}</> as JSX.Element;
}
